import { Component, OnInit, Inject } from "@angular/core";
import { NotificationFeedItem } from "../models/notification-feed-item.model";
import { NotificationModel } from "../models/notification-model.model";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import { NotificationService } from "../services/notification.service";
import { AddendumService } from "../services/addendum.service";
import { Router } from "@angular/router";
import { EventEmitterService } from "../services/event-emitter.service";
import { Constant } from "../core/constant";
import { CryptographyService } from "../services/cryptography.service";
import { ComputedNotificationSeenService } from "../services/computed-notification-seen.service";
import { StandardService } from "../services/standard.service";

@Component({
  selector: "app-notification-list",
  templateUrl: "./notification-list.component.html",
  styleUrls: ["./notification-list.component.scss"],
})
export class NotificationListComponent implements OnInit {
  loading: boolean = false;
  public notificationHistory: NotificationFeedItem[] =
    new Array<NotificationFeedItem>();

  selectedTab: string = "all"; // 'all' | 'standard' | 'exception'

  page = 1;
  pageSize = 5;
  collectionSize: number = 0;

  // Same standardTypeId -> route mapping already used by the "Recent Updates" widget.
  // MarketBestPractices (2) and ABUStandard (7) were mis-mapped to the Operations
  // Standard detail page (wrong data shape). LocalStandard (3) was pointed at
  // /nrbes-view/, which needs an Nrbes row id, not this standard's own id - routed
  // to the generic standard-id-keyed detail page instead.
  standardRedirectMap: any = {
    1: "/global-recommendations-detail-view/",
    2: "/global-recommendations-detail-view/",
    3: "/view-standard-detail/",
    4: "/minimum-standard-detail-view/",
    5: "/view-equipments-standard-detail/",
    6: "/view-equipments-standard-detail/",
    7: "/view-standard-detail/",
    8: "/operations-standards-detail-view/",
  };

  pageSizes: any[] = [
    { name: "Show 5 Items", value: 5 },
    { name: "Show 10 Items", value: 10 },
    { name: "Show 15 Items", value: 15 },
    { name: "Show 20 Items", value: 20 },
    { name: "Show 25 Items", value: 25 },
    { name: "Show 50 Items", value: 50 },
    { name: "Show 100 Items", value: 100 },
  ];

  constructor(
    private notificationHubService: NotificationService,
    @Inject(LOCAL_STORAGE)
    private storage: WebStorageService,
    public addendumService: AddendumService,
    private router: Router,
    private cryptographyService: CryptographyService,
    private eventEmitterService: EventEmitterService,
    private computedNotificationSeenService: ComputedNotificationSeenService,
    private standardService: StandardService
  ) {}

  ngOnInit() {
    this.page = this.standardService.notificationListFiltration.startingIndex || 1;
    this.pageSize =
      this.standardService.notificationListFiltration.pageSize || this.pageSize;
    this.selectedTab = this.standardService.notificationListSelectedTab || "all";
    this.getAllUserNotifications();
  }

  private getAllUserNotifications() {
    this.loading = true;
    this.notificationHistory = new Array<NotificationFeedItem>();
    let notificationGroup = this.cryptographyService.decryptData(
      this.storage.get("notificationGroup")
    );
    let userId = this.cryptographyService.decryptData(
      this.storage.get("userId")
    );
    let userRole = this.cryptographyService.decryptData(
      this.storage.get("userRole")
    );
    this.notificationHubService
      .getUserNotificationFeed(
        notificationGroup,
        userId,
        userRole,
        (this.page - 1) * this.pageSize,
        this.pageSize
      )
      .subscribe((data) => {
        this.collectionSize = data.count;
        data.notifications.forEach((item) => {
          const feedItem = new NotificationFeedItem(item);
          if (!feedItem.notificationId && this.computedNotificationSeenService.isSeen(feedItem)) {
            feedItem.isSeen = true;
          }
          this.notificationHistory.push(feedItem);
        });
        this.loading = false;
      });
  }

  get filteredNotifications(): NotificationFeedItem[] {
    if (!this.notificationHistory) return [];
    if (this.selectedTab === "standard") {
      return this.notificationHistory.filter((item) =>
        this.getNotificationCategory(item).toLowerCase() === "standard"
      );
    } else if (this.selectedTab === "exception") {
      return this.notificationHistory.filter((item) =>
        this.getNotificationCategory(item).toLowerCase() === "exception"
      );
    }
    return this.notificationHistory;
  }

  get groupedNotifications(): { label: string; items: NotificationFeedItem[] }[] {
    const list = this.filteredNotifications;
    if (!list || list.length === 0) return [];

    const todayItems: NotificationFeedItem[] = [];
    const yesterdayItems: NotificationFeedItem[] = [];
    const olderItems: NotificationFeedItem[] = [];

    const now = new Date();
    const todayStr = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterdayStr = todayStr - (24 * 60 * 60 * 1000);

    list.forEach(item => {
      const itemDate = new Date(item.date);
      const itemDayStr = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate()).getTime();

      if (itemDayStr === todayStr) {
        todayItems.push(item);
      } else if (itemDayStr === yesterdayStr) {
        yesterdayItems.push(item);
      } else {
        olderItems.push(item);
      }
    });

    const groups = [];
    if (todayItems.length > 0) groups.push({ label: "Today", items: todayItems });
    if (yesterdayItems.length > 0) groups.push({ label: "Yesterday", items: yesterdayItems });
    if (olderItems.length > 0) groups.push({ label: "Older", items: olderItems });

    if (groups.length === 0 && list.length > 0) {
      groups.push({ label: "Notifications", items: list });
    }

    return groups;
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
    this.standardService.notificationListSelectedTab = tab;
  }

  getNotificationCategory(item: NotificationFeedItem): string {
    if (
      item.category === "NewStandardPublished" ||
      item.category === "StandardModified"
    ) {
      return "Standard";
    }
    return "Exception";
  }

  getAvatarColor(itemOrName: any, index?: number): string {
    // Curated color palette: Light Green, Purple, Blue, Grey
    const colors = [
      "#8b5cf6", // Purple
      "#3b82f6", // Blue
      "#10b981", // Light Green
      "#64748b"  // Grey
    ];

    let str = "";
    if (typeof itemOrName === "string") {
      str = itemOrName;
    } else if (itemOrName) {
      str = (itemOrName.notifiedUser || "") + (itemOrName.id || "") + (itemOrName.message || "");
    }

    if (index !== undefined) {
      str += index;
    }

    if (!str) {
      return colors[(index || 0) % colors.length];
    }

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIdx = Math.abs(hash) % colors.length;
    return colors[colorIdx];
  }

  getSingleInitial(name: string): string {
    if (!name) return "N";
    return name.trim().charAt(0).toUpperCase();
  }

  getTimeAgo(date: Date): string {
    if (!date) return "";
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "15 mins ago";
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hrs ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    const d = new Date(date);
    return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
  }

  rowSelected(item: NotificationFeedItem) {
    if (item.notificationId) {
      // Stored exception-status notification - persist seen state server-side.
      item.isSeen = true;
      this.loading = true;
      const model = new NotificationModel();
      model.id = item.notificationId;
      model.isSeen = true;
      this.notificationHubService.UpdateNotification(model).subscribe(() => {
        this.loading = false;
        this.eventEmitterService.getAlreadyReservedUserNotificationsEmit();
      });
    } else {
      // Computed category (recent standards, expiring exceptions) - no row to
      // persist against, so track "seen" client-side instead.
      item.isSeen = true;
      this.computedNotificationSeenService.markSeen(item);
    }

    if (
      item.category === "NewStandardPublished" ||
      item.category === "StandardModified"
    ) {
      const base =
        this.standardRedirectMap[item.standardTypeId] ||
        "/view-standard-detail/";
      this.router.navigate([base + item.standardId]);
    } else {
      this.router.navigate(["/addendum/" + item.addendumId]);
    }
  }

  get totalPages(): number {
    return Math.ceil(this.collectionSize / this.pageSize) || 1;
  }

  goToPreviousPage() {
    if (this.page > 1) {
      this.page--;
      this.loadNotificationsForPageChange();
    }
  }

  goToNextPage() {
    if (this.page < this.totalPages) {
      this.page++;
      this.loadNotificationsForPageChange();
    }
  }

  selectedItemsPerPageChange(event) {
    this.pageSize = event.value;
    this.page = 1;
    this.standardService.notificationListFiltration.pageSize = this.pageSize;
    this.standardService.notificationListFiltration.startingIndex = this.page;
    this.getAllUserNotifications();
  }

  loadNotificationsForPageChange() {
    this.standardService.notificationListFiltration.startingIndex = this.page;
    this.getAllUserNotifications();
  }
}
